import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useAuth } from '@/stores/auth'
import { usePageVisibility } from '@/stores/uiSettings'
import { hasPermission } from '@shared/permissions'
import { flattenPages, NAV_TREE } from './tree'
import { hubOfHome, openTabs } from './pageHubs'
import { movedHomePath } from '@/app/movedPages'
import type { NavGroup, NavNode, NavPage } from './types'

/** The check the sidebar, the page guard and a hub's tabs share: permission, صلاحيات المستخدمين, إعدادات الإظهار. */
export const pageOpener = (granted: ReadonlySet<string>, isVisible: (pageId: string) => boolean, closed: ReadonlySet<string>) => (p: NavPage) =>
  !closed.has(p.id) && hasPermission(granted, p.permission) && isVisible(p.id)

let byId: Map<string, NavPage> | null = null
/** Every page of the tree by id. */
export const pagesById = (): ReadonlyMap<string, NavPage> => (byId ??= new Map(flattenPages().map(({ page }) => [page.id, page])))

function filterNodes(nodes: NavNode[], canOpen: (p: NavPage) => boolean): NavNode[] {
  const out: NavNode[] = []
  for (const n of nodes) {
    if (n.kind === 'page') {
      // a hub's home is in the sidebar for whoever may open any of its tabs, not only its own screen
      const hub = hubOfHome(n.id)
      // a page that moved into a home stays in the sidebar for whoever may open it but not its new home
      const homePath = n.hidden ? movedHomePath(n.id) : null
      const homePage = homePath ? [...pagesById().values()].find((p) => p.path === homePath) : undefined
      const stays = Boolean(homePage) && !canOpen(homePage!) && canOpen(n)
      if ((!n.hidden || stays) && (hub ? openTabs(hub, pagesById(), canOpen).length > 0 : canOpen(n))) out.push(n)
    } else {
      const children = filterNodes(n.children, canOpen)
      if (children.length) out.push({ ...n, children })
    }
  }
  return out
}

/** Navigation tree restricted to what the signed-in user may open, minus pages an admin hid for them (إعدادات الإظهار). */
export function useVisibleNav(): NavGroup[] {
  const granted = useAuth((s) => s.permissionSet)
  const closed = useClosedPages()
  const isVisible = usePageVisibility()
  return useMemo(() => filterNodes(NAV_TREE, pageOpener(granted, isVisible, closed)) as NavGroup[], [granted, isVisible, closed])
}

/**
 * The pages صلاحيات المستخدمين closed for the signed-in user.
 *
 * It used to be enforced by permissions alone, and a permission is only dropped when EVERY page needing it is
 * hidden - so closing one screen among the many that share `shipments:read` changed nothing on screen. The owner
 * reported it on 2026-09-26 (user farah: the rules were set and she still saw everything). The list itself now comes
 * with the session, and this is the one place that reads it.
 */
export function useClosedPages(): Set<string> {
  const hidden = useAuth((s) => s.user?.hiddenPages)
  return useMemo(() => new Set(hidden ?? []), [hidden])
}

const pathOf = (p: NavPage) => p.path.split('?')[0]!

/** Finds the nav entry for the current location, preferring the entry whose section matches the URL prefix. */
export function useActiveNav(): { page: NavPage; trail: NavGroup[] } | null {
  const { pathname, search } = useLocation()
  return useMemo(() => findNav(pathname, search), [pathname, search])
}

/** The nav entry for any path + query (the active page, or a workspace tab that is not in front). */
export function findNav(pathname: string, search: string): { page: NavPage; trail: NavGroup[] } | null {
  const all = flattenPages()
  const exact = all.filter(({ page }) => {
    const [path, query] = page.path.split('?')
    if (!matchPath({ path: path!, end: true }, pathname)) return false
    return query ? search.includes(query) : true
  })
  if (!exact.length) return null
  // Several entries share a screen (e.g. DRS under Operations and Runners): pick the one whose section id
  // matches the first URL segment, else the one with a query match, else the first.
  const segment = pathname.split('/')[1] ?? ''
  return exact.find(({ trail }) => segment && trail[0]?.id.startsWith(segment.replace(/-.*/, ''))) ?? exact.find(({ page }) => page.path.includes('?')) ?? exact[0]!
}

export function isPageActive(page: NavPage, pathname: string, search: string) {
  const [path, query] = page.path.split('?')
  return Boolean(matchPath({ path: path!, end: true }, pathname)) && (query ? search.includes(query) : !pathOf(page).includes('?'))
}

export interface NavSearchResult {
  page: NavPage
  trail: NavGroup[]
  score: number
}

const normalise = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[\u064B-\u065F\u0670]/g, '').replace(/[أإآ]/g, 'ا').replace(/ة/g, 'ه').replace(/ى/g, 'ي')

export function searchNav(tree: NavGroup[], query: string, limit = 30): NavSearchResult[] {
  const q = normalise(query.trim())
  if (!q) return []
  const results: NavSearchResult[] = []
  for (const { page, trail } of flattenPages(tree)) {
    const hay = [page.label.en, page.label.ar, ...(page.keywords ?? []), ...trail.flatMap((g) => [g.label.en, g.label.ar])].map(normalise)
    let score = 0
    if (hay[0]!.startsWith(q) || hay[1]!.startsWith(q)) score = 3
    else if (hay[0]!.includes(q) || hay[1]!.includes(q)) score = 2
    else if (hay.some((h) => h.includes(q))) score = 1
    if (score) results.push({ page, trail, score })
  }
  const seen = new Set<string>()
  return results
    .sort((a, b) => b.score - a.score)
    .filter((r) => (seen.has(r.page.id) ? false : (seen.add(r.page.id), true)))
    .slice(0, limit)
}
